import { Dialog, SxProps, useMediaQuery, useTheme } from "@mui/material";
import {
  Box,
  Button,
  Color,
  ColorMono,
  Grid,
  Link,
  Typography,
} from "kvweb-ui";
import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import CustomColor from "../../../utils/overrides/color";

// Custom Link component to handle iframe navigation
const IframeSafeLink = ({ href, children, ...props }: any) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Use window.top to navigate in the parent window instead of iframe
    if (window.top && window.top !== window) {
      window.top.location.href = href;
    } else {
      window.open(href, '_blank');
    }
  };

  return (
    <Link href={href} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};

interface IModalConfirmOnInfinityRemainProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  merchantRefCode?: string | undefined;
}
export default function ModalConfirmOnInfinityRemain({
  open,
  onClose,
  onConfirm,
  merchantRefCode,
}: IModalConfirmOnInfinityRemainProps) {
  const theme = useTheme();
  const [stepActive, setStepActive] = useState(0);
  const { t } = useTranslation();
  const steps = useMemo(() => {
    return [
      {
        description: t("PRODUCT.GO_TO_STORE_SETTINGS"),
      },
      {
        description: t("PRODUCT.GO_TO_TRANSACTIONS"),
      },
      {
        description: t("PRODUCT.CLICK_ORDER_DETAIL"),
      },
      {
        description: t("PRODUCT.SELECT_ACCEPT_ORDER_GOODS_OOS"),
      },
    ];
  }, []);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMemo(() => {
    return !isMobile;
  }, [isMobile]);

  const generateLinkPos = () => {
    return `https://${merchantRefCode}.${process.env.REACT_APP_KIOTVIET_DOMAIN}/man/#/PosParameter#tab-transaction`;
  };

  return (
    <Dialog
      open={open}
      PaperProps={{
        style: {
          width: isDesktop ? "730px" : "343px",
          maxWidth: isDesktop ? "730px" : "343px",
          padding: isDesktop ? "24px" : "12px",
          borderRadius: "12px",
          [theme.breakpoints.down("md")]: {
            width: "343px",
            maxWidth: "343px",
            padding: "12px",
          },
        },
      }}
    >
      <Box>
        <Box>
          <Typography textType={"text20S"} color={Color["black-850"]}>
            {t("COMMON.INSTRUCTION")}
          </Typography>
          <Box
            display="flex"
            flexDirection={"column"}
            mt={isDesktop ? "16px" : "4px"}
            sx={{
              flexWrap: "wrap",
              [theme.breakpoints.down("md")]: {
                mt: "4px",
              },
            }}
          >
            {/* <Typography textType="text14R" color={Color["black-750"]}>
              Để bật tính năng{" "}
              <b style={{ color: Color["black-750"] }}>
                “Đặt hàng khi hết tồn kho”
              </b>{" "}
              vui lòng truy cập{" "}
              <Link href={generateLinkPos()} target={"_blank"}>
                Tại đây
              </Link>
            </Typography>
            <Typography textType="text14R" color={Color["black-750"]}>
              Và làm theo hướng dẫn:
            </Typography> */}
            <Typography textType="text14R" color={Color["black-750"]}>
              <Trans
                i18nKey="COMMON.TO_ACTIVE_FEATURE_PRODUCT_OOS_PLEASE_CLICK_HERE"
                components={{
                  a: <IframeSafeLink href={generateLinkPos()} sx={{
                    color: CustomColor["primary-500"],
                  }} />,
                }}
              />
            </Typography>
          </Box>
        </Box>

        <Box
          mt={isDesktop ? "16px" : "8px"}
          display={isDesktop ? "flex" : "block"}
          gap="16px"
          sx={{
            [theme.breakpoints.down("md")]: {
              mt: "8px",
              display: "block",
            },
          }}
        >
          <Grid container spacing={isDesktop ? "20px 0px" : "9px"}>
            {steps.map((step, index) => (
              <Grid item xs={6} md={isDesktop ? 12 : 6} key={index}>
                <Step
                  changeStep={setStepActive}
                  stepNumber={index + 1}
                  isActive={index === stepActive}
                  description={step.description}
                  isDesktop={isDesktop}
                />
              </Grid>
            ))}
          </Grid>
          <Box
            sx={{
              width: isDesktop ? "456px" : "100%",
              minWidth: isDesktop ? "456px" : "100%",
              maxWidth: isDesktop ? "456px" : "319px",
              height: isDesktop ? "292px" : "318px",
              minHeight: isDesktop ? "292px" : "318px",
              maxHeight: isDesktop ? "292px" : "318px",
              display: "flex",
              [theme.breakpoints.down("md")]: {
                mt: "12px",
                width: "100%",
                // minWidth: "319px",
                maxWidth: "319px",
                height: "318px",
                minHeight: "318px",
                maxHeight: "318px",
              },
            }}
          >
            <img
              style={{
                width: isDesktop ? "456px" : "100%",
                height: isDesktop ? "292px" : "318px",
                overflow: "hidden",
                borderRadius: "8px",
                maxWidth: "100%",
              }}
              alt="img"
              src={`${
                process.env.REACT_APP_ADMIN_URL
              }/img/retail/allow-order-out-of-stock/${
                isDesktop ? "desktop" : "mobile"
              }/step${stepActive + 1}.png`}
            />
          </Box>
        </Box>

        <Box
          mt="34px"
          sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <Button
            btnType="outlined"
            sx={{
              width: "142px",
              [theme.breakpoints.down("md")]: {
                flex: 1,
              },
            }}
            onClick={onClose}
          >
            {t("COMMON.BUTTON.CLOSE")}
          </Button>
          <Button
            sx={{
              width: "142px",
              [theme.breakpoints.down("md")]: {
                flex: 1,
              },
            }}
            onClick={onConfirm}
          >
            {t("COMMON.BUTTON.I_TURNED_ON")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

interface IStepProps {
  stepNumber: number;
  isActive: boolean;
  description: string;
  isDesktop: boolean;
  changeStep: (value: number) => void;
}
function Step({
  stepNumber,
  isActive,
  description,
  changeStep,
  isDesktop,
}: IStepProps) {
  const theme = useTheme();
  const {t} = useTranslation()
  return (
    <div
      style={{
        position: "relative",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        gap: "8px",
        borderRadius: "8px",
        backgroundColor: isActive ? CustomColor["primary-50"] : "transparent",
        height: "100%",
        [theme.breakpoints.down("md")]: {
          width: "343px",
          maxWidth: "343px",
          padding: "12px",
        },
      }}
      onClick={() => {
        changeStep(stepNumber - 1);
      }}
    >
      {stepNumber !== 1 && isDesktop && <Icon />}

      <Box
        sx={{
          width: isDesktop ? "24px" : "20px",
          minWidth: isDesktop ? "24px" : "20px",
          height: isDesktop ? "24px" : "20px",
          display: "flex",
          justifyItems: "center",
          alignItems: "center",
          borderRadius: "50%",
          backgroundColor: isActive ? CustomColor["primary-500"] : Color["black-50"],
        }}
      >
        <Typography
          textType="text14R"
          color={isActive ? ColorMono["color-white"] : Color["black-600"]}
          width="100%"
          textAlign="center"
        >
          {stepNumber}
        </Typography>
      </Box>
      <Box>
        <Typography
          textType={isDesktop ? "text14S" : "text12S"}
          color={Color["black-850"]}
        >
          {t("ONBOARDING.STEP", {
            step: stepNumber,
          })}
        </Typography>
        <Typography
          textType={isDesktop ? "text12R" : "text10R"}
          color={Color["black-750"]}
          mt="2px"
        >
          {description}
        </Typography>
      </Box>
    </div>
  );
}

function Icon() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "absolute",
        top: "-17px",
        left: "20px",
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
      }}
    >
      <svg
        width="2"
        height="36"
        viewBox="0 0 2 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 0L1 36"
          stroke={CustomColor["primary-500"]}
          stroke-dasharray="2 2"
        />
      </svg>
    </Box>
  );
}
